import { useState } from 'react';
import { Link } from 'react-router-dom';

import { KTCard, KTCardBody } from '../../../../../_metronic/helpers';
import {
	useApproveFreeRequest,
	useGetFreeRequests,
	useRejectFreeRequest,
} from '../api/freeRequests';
import {
	FreeRequest,
	FreeRequestStatus,
	STATUS_BADGES,
	STATUS_LABELS,
} from '../model/types';

const FILTERS: { value: FreeRequestStatus | 'all'; label: string }[] = [
	{ value: 'new', label: 'Новые' },
	{ value: 'approved', label: 'Одобренные' },
	{ value: 'rejected', label: 'Отклонённые' },
	{ value: 'all', label: 'Все' },
];

const formatDate = (value: string | null) =>
	value ? new Date(value).toLocaleString('ru-RU') : '—';

/**
 * Заявки продавцов на бесплатную подписку.
 *
 * До этого экрана заявка не существовала как сущность: обработчик рассылал
 * менеджерам уведомление и возвращал success. Уведомление было единственным
 * следом — пропустил, и узнать, кто просил, уже неоткуда. Обработать заявку
 * было нечем: кнопка выдачи в карточке пользователя закомментирована, а
 * компонент с рабочей кнопкой нигде не отрисовывался.
 *
 * По умолчанию открываются новые: экран нужен, чтобы разобрать очередь, а
 * не листать историю.
 */
export const FreeRequestsPage = () => {
	const [filter, setFilter] = useState<FreeRequestStatus | 'all'>('new');
	const [rejecting, setRejecting] = useState<FreeRequest | null>(null);
	const [comment, setComment] = useState('');

	const { data, isLoading } = useGetFreeRequests(filter);
	const { mutate: approve, isLoading: isApproving } = useApproveFreeRequest();
	const { mutate: reject, isLoading: isRejecting } = useRejectFreeRequest();

	const items = data?.items ?? [];

	const handleReject = () => {
		if (!rejecting) return;
		reject(
			{ uuid: rejecting.uuid, comment },
			{
				onSettled: () => {
					setRejecting(null);
					setComment('');
				},
			}
		);
	};

	return (
		<KTCard className='w-100'>
			<KTCardBody className='py-4'>
				<div className='d-flex flex-stack mb-6'>
					<h3 className='fw-bold m-0'>Заявки на бесплатную подписку</h3>
					<div className='btn-group'>
						{FILTERS.map(item => (
							<button
								key={item.value}
								type='button'
								className={`btn btn-sm ${
									filter === item.value ? 'btn-primary' : 'btn-light'
								}`}
								onClick={() => setFilter(item.value)}
							>
								{item.label}
							</button>
						))}
					</div>
				</div>

				{isLoading ? (
					<div className='d-flex justify-content-center py-10'>
						<span className='spinner-border text-primary' role='status' />
					</div>
				) : items.length === 0 ? (
					<div className='text-center text-muted py-10'>
						{filter === 'new'
							? 'Новых заявок нет'
							: 'В этой категории пусто'}
					</div>
				) : (
					<div className='table-responsive'>
						<table className='table align-middle table-row-dashed fs-6 gy-4'>
							<thead>
								<tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
									<th>Продавец</th>
									<th>Статус</th>
									<th>Создана</th>
									<th>Решение</th>
									<th className='text-end'>Действия</th>
								</tr>
							</thead>
							<tbody className='fw-semibold text-gray-600'>
								{items.map(item => (
									<tr key={item.uuid}>
										<td>
											{/* Ссылка в карточку: почти всё, что нужно для
											    решения — баланс, роли, история — там. */}
											<Link
												to={`/apps/profile/overview/${item.user_uuid}`}
												className='text-hover-primary'
											>
												{item.user_uuid}
											</Link>
										</td>
										<td>
											<span
												className={`badge ${STATUS_BADGES[item.status]}`}
											>
												{STATUS_LABELS[item.status]}
											</span>
										</td>
										<td>{formatDate(item.created)}</td>
										<td>
											{item.status === 'new' ? (
												'—'
											) : (
												<div className='d-flex flex-column'>
													<span>{formatDate(item.resolved)}</span>
													{item.comment && (
														<span className='text-muted fs-8'>
															{item.comment}
														</span>
													)}
												</div>
											)}
										</td>
										<td className='text-end'>
											{item.status === 'new' && (
												<>
													<button
														type='button'
														className='btn btn-sm btn-success me-2'
														disabled={isApproving}
														onClick={() => approve(item.uuid)}
													>
														Выдать
													</button>
													<button
														type='button'
														className='btn btn-sm btn-light-danger'
														onClick={() => setRejecting(item)}
													>
														Отклонить
													</button>
												</>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</KTCardBody>

			{rejecting && (
				<div
					className='modal fade show d-block'
					style={{ background: 'rgba(0,0,0,.4)' }}
				>
					<div className='modal-dialog modal-dialog-centered'>
						<div className='modal-content'>
							<div className='modal-header'>
								<h5 className='modal-title'>Отклонить заявку</h5>
							</div>
							<div className='modal-body'>
								<label className='form-label fs-7'>
									Причина — для операторов, продавцу не показывается
								</label>
								<textarea
									className='form-control'
									rows={3}
									value={comment}
									onChange={e => setComment(e.target.value)}
								/>
							</div>
							<div className='modal-footer'>
								<button
									type='button'
									className='btn btn-light'
									onClick={() => {
										setRejecting(null);
										setComment('');
									}}
								>
									Отмена
								</button>
								<button
									type='button'
									className='btn btn-danger'
									disabled={isRejecting}
									onClick={handleReject}
								>
									Отклонить
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</KTCard>
	);
};
